// apps/admin/src/App.tsx

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { GlobalErrorBoundary } from '@valutten/ui-common';
import { AdminAuthProvider } from './contexts/AdminContext';
import { PostHogProviderWrapper } from './providers/PostHogProvider';
import { AdminRouteGuard } from './components/AdminRouteGuard';
import { Layout } from './components/Layout';
import { db } from './firebaseConfig';
import { collection, addDoc } from 'firebase/firestore';
import LoginPage from './pages/LoginPage';
import AdminPasswordResetPage from './pages/auth/AdminPasswordResetPage';
import AdminCustomPasswordResetHandler from './pages/auth/AdminCustomPasswordResetHandler';
import EnrollMFAPage from './pages/auth/EnrollMFAPage';
import VerifyMFAPage from './pages/auth/VerifyMFAPage';
import Companies from './pages/Companies';
import CompanyDetailsPage from './pages/CompanyDetailsPage';
import OnboardingPage from './pages/OnboardingPage';
import OnboardingStatusPage from './pages/OnboardingStatusPage';
import MonitoringPage from './pages/MonitoringPage';
import FeatureFlagsPage from './pages/FeatureFlagsPage';
import AdminAuditLogPage from './pages/AdminAuditLogPage';
import SecurityAlertsPage from './pages/SecurityAlertsPage';
import ComplianceReportsPage from './pages/ComplianceReportsPage';
import Analytics from './pages/Analytics';
import BillingPage from './pages/BillingPage';
import TemplatesPage from './pages/TemplatesPage';
import DataIntegrityPage from './pages/DataIntegrityPage';
import LoanLifecyclePage from './pages/LoanLifecyclePage';
import EmailConnectionsPage from './pages/EmailConnectionsPage';
import CrmProvisioningPage from './pages/CrmProvisioningPage';
import MarketingLeadsPage from './pages/MarketingLeadsPage';
import LeadDetailPage from './pages/LeadDetailPage';
import OutreachPage from './pages/OutreachPage';
import OutreachDetailPage from './pages/OutreachDetailPage';
import OnboardingApprovalsPage from './pages/OnboardingApprovalsPage';
import SubscriberVerificationConsolePage from './pages/SubscriberVerificationConsolePage';
import NotFoundPage from './pages/errors/NotFoundPage';
import SharedVerificationReportPage from './pages/SharedVerificationReportPage';
import PendingEntityMappingsPage from './pages/PendingEntityMappingsPage';
import StagingFilesPage from './pages/StagingFilesPage';
import AbnReviewPage from './pages/AbnReviewPage';
import GrowthConsolePage from './pages/GrowthConsolePage';

function App() {
  // Expose Firebase helpers for error boundary
  useEffect(() => {
    (window as any).__FIREBASE_FIRESTORE__ = { collection, addDoc };
  }, []);

  return (
    <GlobalErrorBoundary appName="admin" firebaseDb={db}>
      <AdminAuthProvider>
        <PostHogProviderWrapper>
        <Router>
          <Toaster position="top-right" />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<AdminPasswordResetPage />} />
            <Route path="/reset-password" element={<AdminCustomPasswordResetHandler />} />
            <Route path="/mfa/setup" element={<EnrollMFAPage />} />
            <Route path="/mfa/verify" element={<VerifyMFAPage />} />
            <Route path="/verification/shared/:token" element={<SharedVerificationReportPage />} />
            <Route
              path="/*"
              element={
                <AdminRouteGuard>
                  <Layout />
                </AdminRouteGuard>
              }
            >
              <Route index element={<Navigate to="/companies" replace />} />
              <Route path="companies" element={<Companies />} />
              <Route path="leads" element={<MarketingLeadsPage />} />
              <Route path="leads/:type/:id" element={<LeadDetailPage />} />
              <Route path="outreach" element={<OutreachPage />} />
              <Route path="outreach/:id" element={<OutreachDetailPage />} />
              <Route path="companies/onboard" element={<OnboardingPage />} />
              <Route
                path="companies/onboarding-status/:companyId"
                element={<OnboardingStatusPage />}
              />
              <Route path="companies/:companyId" element={<CompanyDetailsPage />} />
              {/*
                Email-Ingestion architectural fix (PR 4/4) — deep-link targets
                for PR #782's Google Chat alert action buttons.

                /companies/:companyId/pending-entity-mappings →
                  triaged from email_ingestion_entity_auto_learned alerts
                /companies/:companyId/staging-files →
                  triaged from email_ingestion_staging_aged alerts
              */}
              <Route
                path="companies/:companyId/pending-entity-mappings"
                element={<PendingEntityMappingsPage />}
              />
              <Route
                path="companies/:companyId/staging-files"
                element={<StagingFilesPage />}
              />
              {/* Tenant ABN review: deep-link target of the pending_review
                  Google Chat card (leadCaptureAction saveBranding), and the
                  list of every pending ABN. Decisions: reviewTenantAbn. */}
              <Route
                path="companies/:companyId/abn-review"
                element={<AbnReviewPage />}
              />
              <Route path="abn-reviews" element={<AbnReviewPage />} />
              {/* VALUTTEN Growth founder console, read-only
                  (founderListGrowthSignals). Deep-link target of
                  GROWTH_ADMIN_CONSOLE_URL. */}
              <Route path="growth" element={<GrowthConsolePage />} />
              <Route
                path="subscribers/:companyId/verify"
                element={<SubscriberVerificationConsolePage />}
              />
              <Route path="monitoring" element={<MonitoringPage />} />
              <Route path="feature-flags" element={<FeatureFlagsPage />} />
              <Route path="admin-audit" element={<AdminAuditLogPage />} />
              <Route path="security-alerts" element={<SecurityAlertsPage />} />
              <Route path="compliance-reports" element={<ComplianceReportsPage />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="billing" element={<BillingPage />} />
              <Route path="templates" element={<TemplatesPage />} />
              <Route path="data-integrity" element={<DataIntegrityPage />} />
              <Route path="loan-lifecycle" element={<LoanLifecyclePage />} />
              <Route path="email-connections" element={<EmailConnectionsPage />} />
              <Route path="crm-provisioning" element={<CrmProvisioningPage />} />
              <Route path="onboarding-approvals" element={<OnboardingApprovalsPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </Router>
        </PostHogProviderWrapper>
      </AdminAuthProvider>
    </GlobalErrorBoundary>
  );
}

export default App;